import { useQuery } from '@apollo/client';
import { BookmarkIcon, HeartIcon, MessageSquareIcon } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GET_USER_REPLIES_QUERY } from '../../graphql/resolvers/postQueries/getUserReplies.gql';
import { formatDate } from '../../helpers/dateConverters';
import { replyAnalysisPath } from '../../helpers/replyAnalysisPath';
import { truncateReplyPreview } from '../../helpers/truncateReplyPreview';
import type { TextBlock } from '../../interfaces/textEditor.interface';
import './replies.scss';

const PREVIEW_MAX = 240;

/**
 * TRIL-350: the "My replies" page at `/user/:username/replies`. Lists a user's
 * own replies (newest first), each row linking to the reply detail page. The
 * score links to the per-reply analysis (TRIL-367), the parent link returns to
 * the replied-to post, and like/bookmark counts come from the reply's analytics.
 */
export default function UserReplies() {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();

  const { data, loading, error } = useQuery(GET_USER_REPLIES_QUERY, {
    variables: { username: username ?? '' },
    skip: !username,
    fetchPolicy: 'cache-and-network',
  });

  const replies = data?.getUserReplies ?? [];
  const isOwn = !!user?.username && user.username === username;
  const heading = isOwn ? 'Your replies' : `@${username}'s replies`;

  return (
    <div className="REPLIES__PAGE">
      <h1 className="REPLIES__TITLE">{heading}</h1>

      {loading && replies.length === 0 ? (
        <p className="REPLIES__STATE">Loading…</p>
      ) : error ? (
        <p className="REPLIES__STATE">Couldn't load replies.</p>
      ) : replies.length === 0 ? (
        <p className="REPLIES__STATE">
          {isOwn ? "You haven't replied to anything yet." : 'No replies yet.'}
        </p>
      ) : (
        <ul className="REPLIES__LIST">
          {replies.map((reply) => {
            const preview = truncateReplyPreview(
              (reply.textBlocks ?? []) as TextBlock[],
              PREVIEW_MAX,
            );
            const score = typeof reply.score === 'number' ? reply.score : null;
            const vi = reply.viewerInteractions;
            const likeCount = vi?.likeCount ?? 0;
            const bookmarkCount = vi?.bookmarkCount ?? 0;

            return (
              <li key={reply.id} className="REPLY__ROW">
                <Link to={`/reply/${reply.id}`} className="REPLY__ROW__body">
                  <p className="REPLY__ROW__preview">
                    {preview.text || '(empty reply)'}
                  </p>
                </Link>
                <div className="REPLY__ROW__meta">
                  {reply.parentPostId && (
                    <Link
                      to={`/post/${reply.parentPostId}`}
                      className="REPLY__ROW__parent"
                    >
                      <MessageSquareIcon size={14} /> In reply to a post
                    </Link>
                  )}
                  {score !== null && (
                    <Link
                      to={replyAnalysisPath(reply.id ?? '')}
                      className="post-score"
                      title="View full reply analysis"
                    >
                      SQS: {score.toFixed(1)}
                    </Link>
                  )}
                  <span className="REPLY__ROW__stat">
                    <HeartIcon size={14} /> {likeCount}
                  </span>
                  <span className="REPLY__ROW__stat">
                    <BookmarkIcon size={14} /> {bookmarkCount}
                  </span>
                  <span className="REPLY__ROW__date">
                    {formatDate(reply.createdAt as string)}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
